import { IconSearch } from "@tabler/icons-react";
import type { FC } from "react";
import { Input, SearchField } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { SearchModalCloseButton } from "./searchModalCloseButton.js";

export interface SearchModalHeaderProps {
  placeholder: string;
  maxLength?: number;
  autoFocus?: boolean;
  showCloseButton?: boolean;
  /** Off while the modal is collapsed to just this bar, which needs no edge. */
  showDivider?: boolean;
  onClose?: () => void;
}

/**
 * Search input header. Must be rendered inside the modal's `Autocomplete`,
 * which supplies the input value/change handler (via `SearchFieldContext`) and
 * forwards arrow keys and Enter to the results listbox. The field deliberately
 * takes no `onSubmit`: it fires on Enter regardless of which row is focused,
 * which would run it alongside — and after — that row's own action.
 */
export const SearchModalHeader: FC<SearchModalHeaderProps> = (props) => {
  const {
    placeholder,
    maxLength,
    autoFocus = true,
    showCloseButton = true,
    showDivider = true,
    onClose,
  } = props;

  return (
    <>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style scoped>{searchInputStyle}</style>
      <div
        className={twMerge(
          "flex shrink-0 items-center gap-3 py-3 pr-3 pl-5",
          showDivider && "border-b border-border-light",
        )}
      >
        <span className="flex size-6 shrink-0 items-center justify-center text-foreground">
          <IconSearch className="size-4.5" />
        </span>
        <SearchField
          aria-label="Search"
          className="flex min-w-0 flex-1 items-center"
          autoFocus={autoFocus}
        >
          <Input
            placeholder={placeholder}
            maxLength={maxLength}
            className="min-w-0 flex-1 border-0 p-0 text-sm leading-6 text-foreground placeholder:text-muted-foreground focus:ring-0 focus:outline-none"
          />
        </SearchField>
        {showCloseButton ? <SearchModalCloseButton onClose={onClose} /> : null}
      </div>
    </>
  );
};

const searchInputStyle = `
[type="search"]::-webkit-search-cancel-button,
[type="search"]::-webkit-search-decoration {
  -webkit-appearance: none;
}

/* Work around for Tailwind + React Aria focus selector being broken on Autocomplete. */
[type="search"]:focus {
  box-shadow: none;
}
`;
